import useIdentity from "@/features/ui/hooks/use-identity";
import { useEffect, useMemo } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useSetRecoilState } from "recoil";
import { lineSheetSetFamily } from "@/features/line-sheet-sets/helpers/sheet-state";
import useGetLineSheetSet, {
  GetLineSheetSetPathParameter,
  GetLineSheetSetRequest,
} from "./use-get-line-sheet-set";
import { isLineSheetSetUnavailableError } from "@/features/line-sheet-sets/line-sheet-set-error";

export interface UseFetchAndSetLineSheetSetParameter {
  data:
    | {
        lineSheetSetId: number;
      }
    | undefined;
  // 존재하지 않는/접근 불가(404/403) 라인시트를 호출측이 안내 페이지로 처리하는 경우 true.
  // 이 훅 내부의 기본 에러 토스트를 그 경우에만 건너뛴다.
  suppressUnavailableToast?: boolean;
}

export default function useFetchAndSetLineSheetSet({
  data,
  suppressUnavailableToast,
}: UseFetchAndSetLineSheetSetParameter) {
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);
  const { error: showError } = useAppToasts();

  const getLineSheetSetKey = useMemo(():
    | {
        parameter: GetLineSheetSetPathParameter;
        request: GetLineSheetSetRequest;
      }
    | undefined => {
    if (data && company) {
      return {
        parameter: {
          by: company.type,
          companyId: company.id,
          lineSheetSetId: data.lineSheetSetId,
        },
        request: {},
      };
    }
    return undefined;
  }, [data, company]);

  const {
    isLoading: isGetSheetSetLoading,
    isValidating: isGetLineSheetSetValidating,
    data: getLineSheetSetData,
    error: getLineSheetSetError,
    mutate: mutateGetLineSheetSet,
  } = useGetLineSheetSet(
    getLineSheetSetKey?.parameter,
    getLineSheetSetKey?.request,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    if (
      getLineSheetSetError &&
      !(
        suppressUnavailableToast &&
        isLineSheetSetUnavailableError(getLineSheetSetError)
      )
    ) {
      showError(getLineSheetSetError);
    }
  }, [showError, getLineSheetSetError, suppressUnavailableToast]);

  const setLineSheetSetFamily = useSetRecoilState(
    lineSheetSetFamily({
      lineSheetSetId: data?.lineSheetSetId || -1,
    })
  );

  useEffect(() => {
    if (
      !isGetSheetSetLoading &&
      !isGetLineSheetSetValidating &&
      getLineSheetSetData
    ) {
      setLineSheetSetFamily({
        key: {
          lineSheetSetId: getLineSheetSetData.id,
        },
        lineSheetSet: {
          ...getLineSheetSetData,
        },
      });
    }
  }, [
    isGetSheetSetLoading,
    getLineSheetSetData,
    setLineSheetSetFamily,
    isGetLineSheetSetValidating,
  ]);

  return {
    getLineSheetSetError,
    getLineSheetSetData,
    mutateGetLineSheetSet,
  };
}
